import { Editor as EditorRef } from "@tinymce/tinymce-react";
import * as React from "react";
import { useState, useEffect } from "react";
import { emphasisLevelLabels, SSMLTag, SSMLTagsColors } from "shared/constants";
import { useTranslation } from "react-i18next";
import CustomSlider from "shared/components/CustomSlider";
import {
  handleSSMLValueClick,
  isEditorEmpty,
  useEditorSelection,
} from "shared/utils/tts-editor-helper";
import { getCurrentSSMLValue } from "features/TTS/TTSCustomEditor/utils/editorKeyDownHandler";
import { useIsActiveSSMLTag } from "shared/hooks/useSSMLTag";
import { useSSMLMenuDisabled } from "shared/utils/menu-disabling-rules";
import { ReactComponent as EmphasisIcon } from "assets/icons/emphasis-icon.svg";
import MenuWrapper from "../../components/MenuWrapper";
import CustomIconTextButtonMenu from "../../components/CustomIconTextButtonMenu";

interface EmphasisMenuProps {
  editorRef: React.RefObject<EditorRef | null>;
  disabled?: boolean;
}

export default function EmphasisMenu({
  editorRef,
  disabled,
}: EmphasisMenuProps): JSX.Element {
  const { t } = useTranslation();
  const [sliderValue, setSliderValue] = useState(1);
  const isEmphasisTagActive = useIsActiveSSMLTag(SSMLTag.Emphasis);
  const editorEmpty = isEditorEmpty(editorRef);
  const hasSelection = useEditorSelection(editorRef);

  const isSelectionDisabled = useSSMLMenuDisabled(editorRef, SSMLTag.Emphasis);

  useEffect(() => {
    if (isEmphasisTagActive) {
      const currentValue = getCurrentSSMLValue(editorRef, SSMLTag.Emphasis);
      if (currentValue) {
        const valueIndex = emphasisLevelLabels.findIndex(
          (label) => label === currentValue
        );
        if (valueIndex !== -1) {
          setSliderValue(valueIndex);
        }
      }
    }
  }, [isEmphasisTagActive, editorRef]);

  const handleSliderChange = (value: number) => {
    setSliderValue(value);
  };

  const handleSubmit = () => {
    if (!editorRef?.current?.editor) return;

    const emphasisLevel = emphasisLevelLabels[sliderValue];

    handleSSMLValueClick(
      editorRef,
      SSMLTag.Emphasis,
      emphasisLevel,
      SSMLTagsColors.EmphasisTag,
      [SSMLTag.Emphasis]
    );
  };

  const isDisabled =
    disabled ||
    editorEmpty ||
    isSelectionDisabled ||
    (!hasSelection && !isEmphasisTagActive);

  return (
    <CustomIconTextButtonMenu
      width="400px"
      componentId="emphasis"
      menuTitle={t("tts.toolbarMenu.emphasisMenu.title")}
      icon={EmphasisIcon}
      editorRef={editorRef}
      isActive={isEmphasisTagActive}
      isEditorEmpty={editorEmpty}
      shouldBeDisabled={isDisabled}
      tagType={SSMLTag.Emphasis}
      isSelectionDisabled={isSelectionDisabled}
      showChevronIcon
    >
      <MenuWrapper
        id="emphasis-menu-wrapper"
        showDeleteButton={isEmphasisTagActive}
        editorRef={editorRef}
        tagType={SSMLTag.Emphasis}
      >
        <CustomSlider
          defaultValue={1}
          step={1}
          minLabel="reduced"
          maxLabel="x-strong"
          labels={emphasisLevelLabels}
          header={t("tts.toolbarMenu.emphasisMenu.title")}
          onSliderChange={handleSliderChange}
          onSubmit={handleSubmit}
          showCheckButton
          value={sliderValue}
        />
      </MenuWrapper>
    </CustomIconTextButtonMenu>
  );
}
